<template>
	<div class="live-maps-page">
		<header class="breadcrumb">
			<span>Live Map</span>
			<div style="flex-grow: 1"></div>
			<NotificationBell v-if="showCrumbBell" placement="header" />
		</header>

		<div class="main-layout">
			<div class="map-area" @click="closeSidebar">
				<LiveMap
					ref="liveMapRef"
					:devices="dynamicDevices"
					:active-track="activeTrackPoints"
					:active-track-id="activeTrackVin"
					:loading="loading"
					@close-track="handleCloseTrack"
					@date-change="handleDateChange"
				/>

				<!-- Toggle Button -->
				<button class="sidebar-toggle" @click.stop="toggleSidebar">
					<ListIcon :size="20" />
				</button>
			</div>

			<div class="sidebar-area" :class="{ open: isSidebarOpen }" @click.stop>
				<DeviceList
					:devices="dynamicDevices"
					:active-track-id="activeTrackVin"
					@toggle-track="handleToggleTrack"
					@center="centerOnDevice"
					@date-select="handleDateSelectFromList"
					@close="isSidebarOpen = false"
				/>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, inject, Ref } from "vue";
import LiveMap from "./live/LiveMap.vue";
import DeviceList from "./live/DeviceList.vue";
import MapSearch from "./live/MapSearch.vue"; // Imported MapSearch
import axios from "@/axiosInstance";
import { ListIcon } from "lucide-vue-next";
import { useToast } from "vue-toast-notification";
import { useRoute } from "vue-router"; // [NEW]
import NotificationBell from "./NotificationBell.vue";

const isSidebarVisible = inject<Ref<boolean>>("isSidebarOpen")!;
const isDesktop = inject<Ref<boolean>>("isDesktop")!;

const toast = useToast();
const route = useRoute(); // [NEW]
const loading = ref(true);
const devices = ref<any[]>([]);
const isSidebarOpen = ref(false);
const liveMapRef = ref<any>(null);

const showCrumbBell = computed(
	() => !isDesktop.value || (isDesktop.value && !isSidebarVisible.value),
);

// Track state
const activeTrackVin = ref<number | string | null>(null);
const activeTrackPoints = ref<{ lat: number; lng: number }[]>([]);

// Admin logic
const me = ref<any>({});
const isAdmin = computed(() => me.value.is_admin);

const dynamicDevices = computed(() => {
	// Return only dynamic devices (filter out static ones)
	return devices.value.filter((d) => !d.is_static);
});

let pollingInterval: any = null;

onMounted(async () => {
	await fetchMe();
	await fetchDevices();

	// [FIX] Priority: URL Param > Default First Device
	const urlDeviceId = route.query.device;
	let targetDevice = null;

	if (urlDeviceId) {
		// Loose comparison (string vs number)
		const found = dynamicDevices.value.find((d) => d.id == urlDeviceId);
		if (found) targetDevice = found;
	}

	// Fallback: Auto-select first VALID device (with location) if no URL param
	if (
		!targetDevice &&
		!activeTrackVin.value &&
		dynamicDevices.value.length > 0
	) {
		// Try to find first device with actual location data
		const firstActive = dynamicDevices.value.find(
			(d) => d.latest_data?.latitude && d.latest_data?.longitude,
		);
		targetDevice = firstActive || dynamicDevices.value[0];
	}

	if (targetDevice) {
		handleToggleTrack(targetDevice, true); // Suppress toast on init
		// Also center on it if it has location
		if (targetDevice.latest_data?.latitude) {
			centerOnDevice(targetDevice);
		}
	}

	pollingInterval = setInterval(fetchDevices, 10000);
});

async function fetchMe() {
	try {
		const { data } = await axios.get("/auth/me", { withCredentials: true });
		me.value = data;
	} catch {
		me.value = {};
	}
}

async function fetchDevices() {
	try {
		const params: any = {};
		if (isAdmin.value) params.all = true;

		const res = await axios.get("/devices/get-all-graphs", {
			params,
			withCredentials: true,
		});
		if (res.data) {
			// Filter out archived if needed
			const allDevs = res.data.filter((d: any) => d.is_archived === false);

			devices.value = allDevs.map((d: any) => ({
				...d,
				id: Number(d.id),
			}));

			// ──────────────── EXTERNAL STATIC POSITIONS (MOAK) ────────────────
			try {
				const extData = await fetchExternalStaticPositions();
				if (extData) {
					devices.value.forEach((d) => {
						if (!d.is_static) return;
						const candidates = [d.name, d.vin, d.serial_number, d.code].filter(
							Boolean,
						);
						let match = null;
						for (const c of candidates) {
							if (extData[c]) {
								match = extData[c];
								break;
							}
							const partialKey = Object.keys(extData).find((k) =>
								c.includes(k),
							);
							if (partialKey) {
								match = extData[partialKey];
								break;
							}
						}
						if (match && match.latitude && match.longitude) {
							d.location = [match.latitude, match.longitude];

							const newLocation = {
								latitude: match.latitude,
								longitude: match.longitude,
							};

							// [FIX] Update timestamp if available so dashboard reflects "Last Update"
							if (match.timestamp) {
								// Parse Moak timestamp (likely ISO) to Unix Seconds for compatibility
								const tsDate = new Date(match.timestamp);
								if (!isNaN(tsDate.getTime())) {
									const tsSeconds = Math.floor(tsDate.getTime() / 1000);
									(newLocation as any).timestamp = tsSeconds;
									d.updatedAt = tsSeconds;
								}
							}

							d.latest_data = { ...d.latest_data, ...newLocation };
						}
					});
				}
			} catch (e) {
				console.warn("Merge external failed", e);
			}
			// ──────────────────────────────────────────────────────────────────
		}
	} catch (e) {
		console.error("Error fetching devices", e);
	} finally {
		loading.value = false;
	}
}

async function fetchExternalStaticPositions() {
	try {
		// [FIX] Use backend proxy to avoid CORS and handle Auth securely
		// Was: fetch('https://moak.archimede.world/api/static-positions');
		const res = await axios.get("/haystack/static-positions", {
			timeout: 5000,
		});

		// Axios returns data in res.data, fetch returns promise
		return res.data;
	} catch (e) {
		console.warn("External static positions fetch failed", e);
		return null;
	}
}

async function handleToggleTrack(device: any, suppressToast = false) {
	if (activeTrackVin.value === device.id) {
		activeTrackVin.value = null;
		activeTrackPoints.value = [];
		return;
	}

	activeTrackVin.value = device.id;
	loading.value = true;

	try {
		// Calculate today's range (local time)
		const now = new Date();
		const startOfDay = new Date(
			now.getFullYear(),
			now.getMonth(),
			now.getDate(),
			0,
			0,
			0,
		);
		const endOfDay = new Date(
			now.getFullYear(),
			now.getMonth(),
			now.getDate(),
			23,
			59,
			59,
		);

		// Fetch logs instead of daily-track
		const res = await axios.get(`/track/${device.id}/logs`, {
			params: {
				startTime: Math.floor(startOfDay.getTime() / 1000),
				endTime: Math.floor(endOfDay.getTime() / 1000),
				limit: 5000, // High limit for full day
				orderBy: "timestamp",
				orderDirection: "ASC",
			},
			withCredentials: true,
		});

		if (res.data?.logs) {
			activeTrackPoints.value = res.data.logs
				.filter((l: any) => l.latitude && l.longitude)
				.map((l: any) => ({
					lat: Number(l.latitude),
					lng: Number(l.longitude),
					timestamp: l.timestamp,
					speed: Number(l.vehicle_speed || 0),
					rpm: Number(l.engine_rpm || 0),
					fuel: Number(l.fuel_level_percentage || 0),
					odometer: Number(l.high_resolution_total_vehicle_distance || 0),
				}));

			if (activeTrackPoints.value.length === 0 && !suppressToast) {
				toast.info("Nessun tracciato trovato per oggi.");
			}
		} else {
			activeTrackPoints.value = [];
			toast.info("Nessun tracciato disponibile.");
		}
	} catch (e) {
		console.error("Error fetching track", e);
		toast.error("Errore caricamento percorso.");
	} finally {
		loading.value = false;
	}
}

function handleCloseTrack() {
	activeTrackVin.value = null;
	activeTrackPoints.value = [];
}

async function handleDateChange(date: string) {
	if (!activeTrackVin.value) return;

	loading.value = true;
	try {
		// Parse selected date YYYY-MM-DD
		const d = new Date(date);
		const startOfDay = new Date(
			d.getFullYear(),
			d.getMonth(),
			d.getDate(),
			0,
			0,
			0,
		);
		const endOfDay = new Date(
			d.getFullYear(),
			d.getMonth(),
			d.getDate(),
			23,
			59,
			59,
		);

		const res = await axios.get(`/track/${activeTrackVin.value}/logs`, {
			params: {
				startTime: Math.floor(startOfDay.getTime() / 1000),
				endTime: Math.floor(endOfDay.getTime() / 1000),
				limit: 5000,
				orderBy: "timestamp",
				orderDirection: "ASC",
			},
			withCredentials: true,
		});

		if (res.data?.logs) {
			activeTrackPoints.value = res.data.logs
				.filter((l: any) => l.latitude && l.longitude)
				.map((l: any) => ({
					lat: Number(l.latitude),
					lng: Number(l.longitude),
					timestamp: l.timestamp,
					speed: Number(l.vehicle_speed || 0),
					rpm: Number(l.engine_rpm || 0),
					fuel: Number(l.fuel_level_percentage || 0),
					odometer: Number(l.high_resolution_total_vehicle_distance || 0),
				}));

			if (activeTrackPoints.value.length === 0) {
				toast.info(`Nessun tracciato trovato per il ${date}.`);
			}
		}
	} catch (e) {
		console.error("Error fetching track for date", e);
		toast.error("Errore caricamento percorso per la data selezionata.");
	} finally {
		loading.value = false;
	}
}

function centerOnDevice(device: any) {
	if (liveMapRef.value) {
		liveMapRef.value.centerOnDevice(device);
	}
}

async function handleDateSelectFromList(payload: {
	deviceId: number | string;
	date: string;
}) {
	console.log("Date selected from list:", payload);
	const { deviceId, date } = payload;

	// Set active device
	activeTrackVin.value = String(deviceId); // Ensure string/number consistency

	// Load track for that date
	await handleDateChange(date);
}

function toggleSidebar() {
	isSidebarOpen.value = !isSidebarOpen.value;
}

function closeSidebar() {
	isSidebarOpen.value = false;
}

// Map Search Handler
function handleMapSearch(location: {
	lat: number;
	lng: number;
	label: string;
}) {
	console.log("Flying to search location:", location);
	if (liveMapRef.value && liveMapRef.value.mapInstance) {
		// Access Leaflet Map instance directly if possible, or use a method on LiveMap
		// Assuming LiveMap exposes mapInstance or a flyTo method
		// If not, we might need to add it or use props to trigger move

		// Let's see if we can just call flyTo on the map object if it's there
		liveMapRef.value.mapInstance.flyTo([location.lat, location.lng], 16, {
			animate: true,
			duration: 1.5,
		});

		// Optionally add a temporary marker?
		// For now just flying is good enough as per requirement
	} else {
		console.warn("Map instance not found on LiveMap Ref");
	}
}
</script>

<style scoped>
.live-maps-page {
	position: relative;
	width: 100%;
	height: 100dvh;
	height: 100vh; /* Fallback */
	overflow: hidden;
	display: flex;
	flex-direction: column;
}

.breadcrumb {
	flex-shrink: 0;
}

.main-layout {
	flex: 1;
	display: flex;
	position: relative;
	overflow: hidden;
}

.map-area {
	width: 100%;
	height: 100%;
	position: relative;
	z-index: 1;
}

.sidebar-area {
	position: absolute;
	top: 0;
	right: 0;
	width: 350px;
	height: 100%;
	background: #1e2329;
	z-index: 1000;
	transform: translateX(100%);
	transition: transform 0.3s ease-in-out;
	box-shadow: -2px 0 10px rgba(0, 0, 0, 0.5);
}

.sidebar-area.open {
	transform: translateX(0);
}

.sidebar-toggle {
	position: absolute;
	top: 90px;
	right: 9px;
	z-index: 999;
	background: #1e2329;
	border: 1px solid #444;
	color: #fff;
	padding: 8px;
	border-radius: 4px;
	cursor: pointer;
	box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
}

.sidebar-toggle:hover {
	background: #2a2f34;
}
</style>
